import React from 'react';

import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { CommissionOption, RuleCondition } from '~/consts/agentHub';

import type { RewardConditionOperator, RewardConditionType } from '~/types/services/agentHub';

import AgentHubCommissionsAffiliationInput from '../Commission/AgentHubCommissions/AgentHubCommissionsAffiliationInput';
import AgentHubCommissionsAgencyInput from '../Commission/AgentHubCommissions/AgentHubCommissionsAgencyInput';
import AgentHubCommissionsAgentInput from '../Commission/AgentHubCommissions/AgentHubCommissionsAgentInput';
import { CONDITION_OPERATOR_SYMBOL, createFormId } from '../Commission/AgentHubCommissions/CommissionDialog.types';

import {
  NUMERIC_REWARD_CONDITION_TYPES,
  REWARD_CONDITION_LABELS,
  REWARD_CONDITION_OPERATORS,
  REWARD_CONDITION_TYPES,
  REWARD_PRODUCT_TYPES,
  SUBJECT_REWARD_CONDITION_TYPES,
  buildRewardConditionsSummary,
  createEmptyRewardCondition,
  isEqOnlyRewardConditionType,
} from './rewardForm';
import type { RewardConditionForm } from './rewardForm';

interface Props {
  conditions: Array<RewardConditionForm>;
  operator: 'AND' | 'OR';
  onChange: (conditions: Array<RewardConditionForm>) => void;
}

function existingOption(threshold: string): CommissionOption {
  return threshold ? [{ value: threshold, label: threshold }] : [];
}

function sanitizeNumericThreshold(type: RewardConditionType, value: string) {
  if (type === 'bookingCount') {
    return value.replace(/[^\d]/g, '');
  }

  const cleaned = value.replace(/[^\d.]/g, '');
  const [whole, ...fractionParts] = cleaned.split('.');
  if (fractionParts.length === 0) {
    return whole;
  }
  return `${whole}.${fractionParts.join('').replace(/\./g, '')}`;
}

function RewardConditionsEditor({ conditions, operator, onChange }: Props) {
  const updateCondition = (id: string, partial: Partial<RewardConditionForm>) => {
    onChange(conditions.map((condition) => (condition.id === id ? { ...condition, ...partial } : condition)));
  };

  const handleSubjectChange = (id: string, type: RewardConditionType, condition: RuleCondition) => {
    const selected = condition.value[0];
    updateCondition(id, {
      type,
      threshold: selected?.value ? String(selected.value) : '',
    });
  };

  const rewardBreakdown = buildRewardConditionsSummary(conditions, operator);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="button" color="secondary">
          Reward conditions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          At least one qualification condition is required. Product types filter eligible bookings: any selected type
          matches. AND/OR combines the remaining conditions.
        </Typography>
      </Box>
      {conditions.map((condition) => (
        <Stack key={condition.id} direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel shrink>Type</InputLabel>
            <Select
              label="Type"
              value={condition.type}
              onChange={(event) => {
                const type = event.target.value as RewardConditionType;
                updateCondition(condition.id, {
                  type,
                  threshold: '',
                  operator: isEqOnlyRewardConditionType(type) ? 'eq' : condition.operator,
                });
              }}
            >
              {REWARD_CONDITION_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {REWARD_CONDITION_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink>Operator</InputLabel>
            <Select
              label="Operator"
              value={
                isEqOnlyRewardConditionType(condition.type) && condition.operator !== 'eq' ? 'eq' : condition.operator
              }
              onChange={(event) =>
                updateCondition(condition.id, { operator: event.target.value as RewardConditionOperator })
              }
            >
              {(isEqOnlyRewardConditionType(condition.type)
                ? REWARD_CONDITION_OPERATORS.filter((value) => value === 'eq')
                : REWARD_CONDITION_OPERATORS
              ).map((operator) => (
                <MenuItem key={operator} value={operator}>
                  {CONDITION_OPERATOR_SYMBOL[operator]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {condition.type === 'productType' ? (
            <FormControl required sx={{ minWidth: 260 }}>
              <InputLabel>Product type</InputLabel>
              <Select
                label="Product type"
                value={condition.threshold}
                onChange={(event) => updateCondition(condition.id, { threshold: event.target.value })}
              >
                {REWARD_PRODUCT_TYPES.map((productType) => (
                  <MenuItem key={productType} value={productType}>
                    {productType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : SUBJECT_REWARD_CONDITION_TYPES.has(condition.type) ? (
            <Box sx={{ minWidth: 260, flex: 1 }}>
              {condition.type === 'agency' && (
                <AgentHubCommissionsAgencyInput
                  existingConditions={existingOption(condition.threshold)}
                  setCreatedConditions={(ruleCondition) => handleSubjectChange(condition.id, 'agency', ruleCondition)}
                />
              )}
              {condition.type === 'agent' && (
                <AgentHubCommissionsAgentInput
                  existingConditions={existingOption(condition.threshold)}
                  setCreatedConditions={(ruleCondition) => handleSubjectChange(condition.id, 'agent', ruleCondition)}
                />
              )}
              {condition.type === 'affiliation' && (
                <AgentHubCommissionsAffiliationInput
                  existingConditions={existingOption(condition.threshold)}
                  setCreatedConditions={(ruleCondition) =>
                    handleSubjectChange(condition.id, 'affiliation', ruleCondition)
                  }
                />
              )}
            </Box>
          ) : (
            <TextField
              label="Threshold"
              type="number"
              inputProps={condition.type === 'bookingCount' ? { min: 0, step: 1 } : { min: 0, step: 'any' }}
              value={condition.threshold}
              onChange={(event) =>
                updateCondition(condition.id, {
                  threshold: NUMERIC_REWARD_CONDITION_TYPES.has(condition.type)
                    ? sanitizeNumericThreshold(condition.type, event.target.value)
                    : event.target.value,
                })
              }
              required
              sx={{ minWidth: 160 }}
            />
          )}
          <Button
            variant="text"
            onClick={() => onChange(conditions.filter((current) => current.id !== condition.id))}
            disabled={conditions.length === 1}
          >
            Remove
          </Button>
        </Stack>
      ))}
      <Button variant="text" onClick={() => onChange(conditions.concat(createEmptyRewardCondition(createFormId())))}>
        Add condition
      </Button>
      {rewardBreakdown.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 1, p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="button" color="secondary">
              Reward breakdown
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">{rewardBreakdown}</Typography>
            </Stack>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export default RewardConditionsEditor;
